import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, FilterIcon, EyeIcon, RefreshCwIcon, XCircleIcon, DownloadIcon, ChevronDownIcon, CheckCircleIcon, ClockIcon, AlertTriangleIcon } from 'lucide-react';
import { formatNaira } from '../../utils/formatCurrency';
import { formatDate } from '../../utils/dateUtils';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Tabs } from '../../components/ui/Tabs';
import { Table } from '../../components/ui/Table';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { Modal, ModalFooter } from '../../components/ui/Modal';
import { MetricGrid } from '../../components/features/MetricCard';
// Mock refund data
const mockRefunds = [{
  id: 'REF-001',
  ticketNumber: 'ABC123',
  passenger: 'Chioma Okonkwo',
  email: 'chioma@email.com',
  amount: 45000,
  option: 'store_credit',
  status: 'completed',
  airline: 'Air Peace',
  createdAt: '2024-02-14T10:30:00'
}, {
  id: 'REF-002',
  ticketNumber: 'DEF456',
  passenger: 'Emeka Eze',
  email: 'emeka@email.com',
  amount: 52000,
  option: 'bank_payout',
  status: 'processing',
  airline: 'Arik Air',
  createdAt: '2024-02-14T09:15:00'
}, {
  id: 'REF-003',
  ticketNumber: 'GHI789',
  passenger: 'Ngozi Nwosu',
  email: 'ngozi@email.com',
  amount: 38000,
  option: 'store_credit',
  status: 'pending',
  airline: 'Dana Air',
  createdAt: '2024-02-14T08:45:00'
}, {
  id: 'REF-004',
  ticketNumber: 'JKL012',
  passenger: 'Adebayo Ogundimu',
  email: 'adebayo@email.com',
  amount: 61000,
  option: 'bank_payout',
  status: 'failed',
  airline: 'Ibom Air',
  createdAt: '2024-02-13T16:20:00'
}, {
  id: 'REF-005',
  ticketNumber: 'MNO345',
  passenger: 'Fatima Mohammed',
  email: 'fatima@email.com',
  amount: 48000,
  option: 'store_credit',
  status: 'completed',
  airline: 'Air Peace',
  createdAt: '2024-02-13T14:10:00'
}];
const statusTabs = [{
  id: 'all',
  label: 'All',
  count: 156
}, {
  id: 'pending',
  label: 'Pending',
  count: 12
}, {
  id: 'processing',
  label: 'Processing',
  count: 8
}, {
  id: 'completed',
  label: 'Completed',
  count: 132
}, {
  id: 'failed',
  label: 'Failed',
  count: 4
}];
// Timeline component for audit trail
function AuditTimeline({
  events
}: {
  events: {
    label: string;
    time: string;
    status: 'complete' | 'current' | 'pending';
  }[];
}) {
  return <div className="space-y-4" data-id="element-849">
      {events.map((event, index) => <motion.div key={index} initial={{
      opacity: 0,
      x: -20
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      delay: index * 0.1
    }} className="flex items-start gap-4" data-id="element-850">
          <div className="relative" data-id="element-851">
            <motion.div className={`w-3 h-3 rounded-full ${event.status === 'complete' ? 'bg-success-500' : event.status === 'current' ? 'bg-primary-500' : 'bg-surface-300'}`} animate={event.status === 'current' ? {
          scale: [1, 1.2, 1]
        } : {}} transition={{
          duration: 1.5,
          repeat: Infinity
        }} data-id="element-852" />
            {index < events.length - 1 && <div className={`absolute top-3 left-1/2 -translate-x-1/2 w-0.5 h-8 ${event.status === 'complete' ? 'bg-success-200' : 'bg-surface-200'}`} data-id="element-853" />}
          </div>
          <div className="flex-1 pb-4" data-id="element-854">
            <p className="text-body-sm font-medium text-surface-900" data-id="element-855">
              {event.label}
            </p>
            <p className="text-caption text-surface-500" data-id="element-856">{event.time}</p>
          </div>
        </motion.div>)}
    </div>;
}
export function RefundManagement() {
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRefund, setSelectedRefund] = useState<(typeof mockRefunds)[0] | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const metrics = [{
    label: 'Total Refunds',
    value: '156',
    change: {
      value: 12.5,
      trend: 'up' as const
    },
    icon: <CheckCircleIcon className="w-5 h-5" data-id="element-857" />,
    iconColor: 'bg-success-100 text-success-600'
  }, {
    label: 'Pending',
    value: '12',
    change: {
      value: -8.2,
      trend: 'down' as const
    },
    icon: <ClockIcon className="w-5 h-5" data-id="element-858" />,
    iconColor: 'bg-warning-100 text-warning-600'
  }, {
    label: 'Processing',
    value: '8',
    icon: <RefreshCwIcon className="w-5 h-5" data-id="element-859" />,
    iconColor: 'bg-primary-100 text-primary-600'
  }, {
    label: 'Failed',
    value: '4',
    icon: <AlertTriangleIcon className="w-5 h-5" data-id="element-860" />,
    iconColor: 'bg-error-100 text-error-600'
  }];
  const columns = [{
    key: 'id',
    header: 'Refund ID',
    sortable: true
  }, {
    key: 'ticketNumber',
    header: 'Ticket',
    sortable: true
  }, {
    key: 'passenger',
    header: 'Passenger',
    sortable: true
  }, {
    key: 'amount',
    header: 'Amount',
    sortable: true,
    render: (value: unknown) => <span className="tabular-nums font-semibold text-surface-900" data-id="element-861">
          {formatNaira(value as number)}
        </span>
  }, {
    key: 'option',
    header: 'Type',
    render: (value: unknown) => <span className={`px-2 py-1 rounded-md text-caption font-medium ${value === 'store_credit' ? 'bg-accent-100 text-accent-700' : 'bg-primary-100 text-primary-700'}`} data-id="element-862">
          {(value as string).replace('_', ' ')}
        </span>
  }, {
    key: 'status',
    header: 'Status',
    render: (value: unknown) => {
      const status = value as string;
      const badgeStatus = status === 'completed' ? 'complete' : status === 'processing' ? 'processing' : status === 'failed' ? 'failed' : 'pending';
      return <StatusBadge status={badgeStatus} size="sm" data-id="element-863">
            {status}
          </StatusBadge>;
    }
  }, {
    key: 'createdAt',
    header: 'Created',
    sortable: true,
    render: (value: unknown) => <span className="text-body-sm text-surface-600" data-id="element-864">
          {formatDate(value as string, {
        includeTime: true
      })}
        </span>
  }, {
    key: 'actions',
    header: '',
    render: (_: unknown, row: (typeof mockRefunds)[0]) => <div className="flex items-center gap-1" data-id="element-865">
          <motion.button onClick={e => {
        e.stopPropagation();
        setSelectedRefund(row);
      }} className="p-2 text-surface-400 hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-colors" whileHover={{
        scale: 1.1
      }} whileTap={{
        scale: 0.9
      }} data-id="element-866">
            <EyeIcon className="w-4 h-4" data-id="element-867" />
          </motion.button>
          {row.status === 'failed' && <motion.button className="p-2 text-surface-400 hover:text-success-500 hover:bg-success-50 rounded-lg transition-colors" whileHover={{
        scale: 1.1
      }} whileTap={{
        scale: 0.9
      }} data-id="element-868">
              <RefreshCwIcon className="w-4 h-4" data-id="element-869" />
            </motion.button>}
          {row.status === 'pending' && <motion.button className="p-2 text-surface-400 hover:text-error-500 hover:bg-error-50 rounded-lg transition-colors" whileHover={{
        scale: 1.1
      }} whileTap={{
        scale: 0.9
      }} data-id="element-870">
              <XCircleIcon className="w-4 h-4" data-id="element-871" />
            </motion.button>}
        </div>
  }];
  const filteredRefunds = mockRefunds.filter(refund => {
    if (activeTab !== 'all' && refund.status !== activeTab) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return refund.id.toLowerCase().includes(query) || refund.ticketNumber.toLowerCase().includes(query) || refund.passenger.toLowerCase().includes(query);
    }
    return true;
  });
  const handleExport = async () => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsExporting(false);
  };
  const auditEvents = selectedRefund ? [{
    label: 'Refund initiated',
    time: formatDate(selectedRefund.createdAt, {
      includeTime: true
    }),
    status: 'complete' as const
  }, {
    label: 'KYC verified via NIBSS',
    time: formatDate(selectedRefund.createdAt, {
      includeTime: true
    }),
    status: 'complete' as const
  }, {
    label: selectedRefund.status === 'completed' ? 'Refund completed' : selectedRefund.status === 'processing' ? 'Processing refund' : 'Awaiting processing',
    time: formatDate(selectedRefund.createdAt, {
      includeTime: true
    }),
    status: selectedRefund.status === 'completed' ? 'complete' as const : selectedRefund.status === 'processing' ? 'current' as const : 'pending' as const
  }] : [];
  return <div className="space-y-6" data-id="element-872">
      {/* Metrics */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} data-id="element-873">
        <MetricGrid metrics={metrics} columns={4} data-id="element-874" />
      </motion.div>

      {/* Search & Filters */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} data-id="element-875">
        <Card variant="elevated" padding="md" className="shadow-lg border-0" data-id="element-876">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-877">
            <div className="flex-1" data-id="element-878">
              <Input placeholder="Search by ID, ticket, or passenger..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} leftIcon={<SearchIcon className="w-4 h-4" data-id="element-880" />} data-id="element-879" />
            </div>
            <div className="flex gap-2" data-id="element-881">
              <motion.div whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-882">
                <Button variant="secondary" icon={<FilterIcon className="w-4 h-4" data-id="element-884" />} onClick={() => setShowFilters(!showFilters)} className={showFilters ? 'bg-primary-50 text-primary-600 border-primary-200' : ''} data-id="element-883">
                  Filters
                  <ChevronDownIcon className={`w-4 h-4 ml-1 transition-transform ${showFilters ? 'rotate-180' : ''}`} data-id="element-885" />
                </Button>
              </motion.div>
              <motion.div whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-886">
                <Button variant="secondary" icon={<DownloadIcon className="w-4 h-4" data-id="element-888" />} onClick={handleExport} loading={isExporting} data-id="element-887">
                  Export
                </Button>
              </motion.div>
            </div>
          </div>

          <AnimatePresence data-id="element-889">
            {showFilters && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} transition={{
            duration: 0.2
          }} className="overflow-hidden" data-id="element-890">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4 pt-4 border-t border-surface-100" data-id="element-891">
                  <Select label="Airline" placeholder="All airlines" options={[{
                value: '',
                label: 'All Airlines'
              }, {
                value: 'air-peace',
                label: 'Air Peace'
              }, {
                value: 'arik-air',
                label: 'Arik Air'
              }, {
                value: 'dana-air',
                label: 'Dana Air'
              }]} value="" onChange={() => {}} data-id="element-892" />
                  <Input label="From Date" type="date" data-id="element-893" />
                  <Input label="To Date" type="date" data-id="element-894" />
                </div>
              </motion.div>}
          </AnimatePresence>
        </Card>
      </motion.div>

      {/* Tabs & Table */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} data-id="element-895">
        <Card variant="elevated" padding="none" className="shadow-lg border-0 overflow-hidden" data-id="element-896">
          <div className="p-4 border-b border-surface-100" data-id="element-897">
            <Tabs tabs={statusTabs} activeTab={activeTab} onTabChange={setActiveTab} data-id="element-898" />
          </div>
          <Table columns={columns} data={filteredRefunds} keyExtractor={row => row.id} onRowClick={setSelectedRefund} data-id="element-899" />
        </Card>
      </motion.div>

      {/* Detail Modal */}
      <Modal isOpen={!!selectedRefund} onClose={() => setSelectedRefund(null)} title="Refund Details" size="lg" data-id="element-900">
        {selectedRefund && <>
            <div className="space-y-6" data-id="element-901">
              {/* Status Banner */}
              <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} className={`p-4 rounded-xl ${selectedRefund.status === 'completed' ? 'bg-success-50 border border-success-200' : selectedRefund.status === 'failed' ? 'bg-error-50 border border-error-200' : selectedRefund.status === 'processing' ? 'bg-primary-50 border border-primary-200' : 'bg-warning-50 border border-warning-200'}`} data-id="element-902">
                <div className="flex items-center justify-between" data-id="element-903">
                  <StatusBadge status={selectedRefund.status === 'completed' ? 'complete' : selectedRefund.status === 'failed' ? 'failed' : selectedRefund.status === 'processing' ? 'processing' : 'pending'} data-id="element-904">
                    {selectedRefund.status}
                  </StatusBadge>
                  <span className="text-body-sm text-surface-600" data-id="element-905">
                    {formatDate(selectedRefund.createdAt, {
                  includeTime: true
                })}
                  </span>
                </div>
              </motion.div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4" data-id="element-906">
                {[{
              label: 'Refund ID',
              value: selectedRefund.id
            }, {
              label: 'Ticket Number',
              value: selectedRefund.ticketNumber
            }, {
              label: 'Passenger',
              value: selectedRefund.passenger
            }, {
              label: 'Email',
              value: selectedRefund.email
            }, {
              label: 'Amount',
              value: formatNaira(selectedRefund.amount),
              highlight: true
            }, {
              label: 'Refund Type',
              value: selectedRefund.option.replace('_', ' ')
            }, {
              label: 'Airline',
              value: selectedRefund.airline
            }].map((item, index) => <motion.div key={item.label} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: index * 0.05
            }} className="p-3 bg-surface-50 rounded-lg" data-id="element-907">
                    <p className="text-body-sm text-surface-500 mb-1" data-id="element-908">
                      {item.label}
                    </p>
                    <p className={`text-body-md font-semibold ${item.highlight ? 'text-primary-600' : 'text-surface-900'} ${item.label === 'Amount' ? 'tabular-nums' : ''}`} data-id="element-909">
                      {item.value}
                    </p>
                  </motion.div>)}
              </div>

              {/* Audit Trail */}
              <div data-id="element-910">
                <h4 className="text-heading-sm font-semibold text-surface-900 mb-4" data-id="element-911">
                  Audit Trail
                </h4>
                <AuditTimeline events={auditEvents} data-id="element-912" />
              </div>
            </div>

            <ModalFooter data-id="element-913">
              <Button variant="secondary" onClick={() => setSelectedRefund(null)} data-id="element-914">
                Close
              </Button>
              {selectedRefund.status === 'failed' && <Button icon={<RefreshCwIcon className="w-4 h-4" data-id="element-916" />} data-id="element-915">
                  Retry Refund
                </Button>}
            </ModalFooter>
          </>}
      </Modal>
    </div>;
}